import axios, { AxiosResponse } from "axios";

import {
	getRequest,
	postRequest,
	putRequest,
	deleteRequest
} from "@/Utils/Service/BackendService";

import { DeletedRecord, ZomentumEntities } from "@/Contracts";
import {
	Product,
	ProductsRequest,
	ProductsResponse,
	ProductSearchRequest,
	PricingSearchRequest,
	ProductSearchResponse,
	AddOrEditProductRequest,
	ProductTemplates,
	MergeDuplicateProductsRequest,
	SelfProductPricingSearchResponse,
	QuoteBlockProductPricingRequest
} from "@/Contracts/Products";
import {
	UnifiedSearchRequest,
	UnifiedSearchResponse,
	UnifiedPricingRequest,
	UnifiedPricingResponse,
	UnifiedAggregationsRequest,
	UnifiedAggregationsResponse
} from "@/Contracts/Products/UnifiedSearch";
import { getTableQueryFilters, includeChildEntities } from "@/Contracts/Utils";
import {
	CustomViewsMetadata,
	CustomViewResponse,
	CustomView,
	ListPagePostRequest
} from "@/Contracts/UI";
import { ProductSource } from "@/Contracts/Products";

const getProductsUrl = "/v2/items";
const searchProductsUrl = "/v2/items/search";
const searchPricingsUrl = "/v2/items/products/pricing";
const searchSelfProductPricingUrl = "/v2/items/pricing";
const deleteProductUrl = "/v2/items/{product_id}";
const addProductUrl = "/v2/items";
const editProductUrl = "/v2/items/{product_id}";
const updateProductTemplatesUrl = "/v2/items/update/template";
const getProductDetailsUrl = "/v2/items/{product_id}";
const getAllProductViewURL = "/v2/views?entity_type={entity_type}";
const getProductViewMetadataURL =
	"/v2/views/metadata?entity_type={entity_type}";
const getProductListPostURL = "/v2/items/view";
const createProductViewURL = "/v2/views";
const updateProductViewURL = "/v2/views/{view_id}";
const deleteProductViewURL = "v2/views/{view_id}";
const getDuplicateProductsURL = "/v2/items/{product_id}/duplicates";
const mergeDuplicateProductsURL = "/v2/items/{product_id}/merge";
const getUpdatedEtilizeProductURL = "/v2/items/{etilize_id}";
const getQuoteProductPricing = "/v2/items/{product_id}/quote/pricings";
const unifiedSearchProductsUrl = "/v2/items/unified-products/search";
const unifiedPricingUrl = "/v2/items/unified-products/pricing";
const unifiedAggregationsUrl = "/v2/items/unified-products/aggregations";

export const getProducts = async (
	productsRequest: ProductsRequest
): Promise<ProductsResponse> => {
	try {
		let url = getProductsUrl;
		if (productsRequest.filters.length > 0) {
			const appliedFilters = getTableQueryFilters(
				productsRequest.filters,
				false
			);
			url =
				url +
				"?" +
				appliedFilters +
				(productsRequest.keywords && productsRequest.keywords.length > 0
					? `&q=${productsRequest.keywords}`
					: "");
		} else {
			url =
				url +
				(productsRequest.keywords && productsRequest.keywords.length > 0
					? `?q=${productsRequest.keywords}`
					: "");
		}
		const response: AxiosResponse<ProductsResponse> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const searchProducts = async (
	productSearchRequest: ProductSearchRequest
): Promise<ProductSearchResponse> => {
	try {
		const response: AxiosResponse<ProductSearchResponse> = await axios(
			postRequest(
				searchProductsUrl,
				productSearchRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const searchPricing = async (
	pricingSearchRequest: PricingSearchRequest
): Promise<ProductSearchResponse> => {
	try {
		const response = await axios(
			postRequest(
				searchPricingsUrl,
				pricingSearchRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const searchSelfProductPricing = async (
	itemIds: string[]
): Promise<SelfProductPricingSearchResponse> => {
	try {
		const data = { item_ids: itemIds };
		const response = await axios(
			postRequest(searchSelfProductPricingUrl, data, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const searchUnifiedProducts = async (
	request: UnifiedSearchRequest
): Promise<UnifiedSearchResponse> => {
	try {
		const response: AxiosResponse<UnifiedSearchResponse> = await axios(
			postRequest(unifiedSearchProductsUrl, request, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const searchUnifiedProductsPricing = async (
	request: UnifiedPricingRequest
): Promise<UnifiedPricingResponse> => {
	try {
		const response: AxiosResponse<UnifiedPricingResponse> = await axios(
			postRequest(unifiedPricingUrl, request, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getUnifiedProductsAggregations = async (
	request: UnifiedAggregationsRequest
): Promise<UnifiedAggregationsResponse> => {
	try {
		const response: AxiosResponse<UnifiedAggregationsResponse> =
			await axios(
				postRequest(unifiedAggregationsUrl, request, true, false, false)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteProduct = async (
	product: Product
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(
				deleteProductUrl.replace("{product_id}", product.id),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addProduct = async (
	addProductRequest: AddOrEditProductRequest
): Promise<Product> => {
	try {
		const response: AxiosResponse<Product> = await axios(
			postRequest(addProductUrl, addProductRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editProduct = async (
	product: AddOrEditProductRequest
): Promise<Product> => {
	try {
		if (product.id === undefined) throw new Error("Product ID is required");
		const response: AxiosResponse<Product> = await axios(
			putRequest(
				editProductUrl.replace("{product_id}", product.id),
				product,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateProductTemplates = async (
	productTemplates: ProductTemplates
): Promise<Product> => {
	try {
		const response: AxiosResponse<Product> = await axios(
			postRequest(
				updateProductTemplatesUrl,
				productTemplates,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

/* TODO: Change source from string to an enum. */
export const getProductDetails = async (
	productId: string,
	source?: string
): Promise<Product> => {
	try {
		const response: AxiosResponse<Product> = await axios(
			getRequest(
				getProductDetailsUrl
					.replace("{product_id}", encodeURIComponent(productId))
					.concat(
						`${
							source === ProductSource.Etilize ||
							source === "marketplace" ||
							source === ProductSource.Icecat ||
							source === ProductSource.Amazon ||
							source === ProductSource.CustomDistributor ||
							source === ProductSource.DickerData
								? `?product_source=${source}`
								: ""
						}`
					),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getQuoteBlockProductPricing = async (
	quoteBlockProductPricingRequest: QuoteBlockProductPricingRequest
): Promise<Product> => {
	try {
		const url =
			getQuoteProductPricing.replace(
				"{product_id}",
				quoteBlockProductPricingRequest.product_id
			) +
			"?" +
			"mfg_no=" +
			`${encodeURIComponent(quoteBlockProductPricingRequest.mfg_no)}` +
			"&sku=" +
			`${encodeURIComponent(quoteBlockProductPricingRequest.sku)}` +
			"&pricing_id=" +
			`${quoteBlockProductPricingRequest.pricing_id}`;
		const response: AxiosResponse<Product> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getProductViewMetadata =
	async (): Promise<CustomViewsMetadata> => {
		try {
			const response: AxiosResponse<CustomViewsMetadata> = await axios(
				getRequest(
					getProductViewMetadataURL.replace(
						"{entity_type}",
						ZomentumEntities.Product
					),
					null,
					true,
					false,
					false
				)
			);
			return response.data;
		} catch (error) {
			console.error(error);
			return Promise.reject(error);
		}
	};

export const getAllProductViews = async (): Promise<CustomViewResponse> => {
	try {
		const response: AxiosResponse<CustomViewResponse> = await axios(
			getRequest(
				getAllProductViewURL.replace(
					"{entity_type}",
					ZomentumEntities.Product
				),
				null,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getProductListPost = async (
	productRequest: ListPagePostRequest
): Promise<ProductsResponse> => {
	try {
		const includedChildEntities = includeChildEntities(
			productRequest.included_child_entities
		);

		const response: AxiosResponse<ProductsResponse> = await axios(
			postRequest(
				getProductListPostURL + `?${includedChildEntities}`,
				productRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const createProductView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			postRequest(
				createProductViewURL,
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const updateProductView = async (
	customViewRequest: CustomView
): Promise<CustomView> => {
	try {
		const response: AxiosResponse<CustomView> = await axios(
			putRequest(
				updateProductViewURL.replace("{view_id}", customViewRequest.id),
				customViewRequest,
				true,
				false,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const deleteProductView = async (
	customViewId: string
): Promise<unknown> => {
	try {
		const response: AxiosResponse<unknown> = await axios(
			deleteRequest(
				deleteProductViewURL.replace("{view_id}", customViewId),
				null,
				true,
				false
			)
		);

		return response.data;
	} catch (error) {
		console.error(error);
		return Promise.reject(error);
	}
};

export const getDuplicateProducts = async (
	productId: string
): Promise<ProductsResponse> => {
	try {
		const response: AxiosResponse<ProductsResponse> = await axios(
			getRequest(
				getDuplicateProductsURL.replace("{product_id}", productId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const mergeDuplicateProducts = async (
	productId: string,
	mergeDuplicateProductsRequest: MergeDuplicateProductsRequest
): Promise<Product> => {
	try {
		const response: AxiosResponse<Product> = await axios(
			putRequest(
				mergeDuplicateProductsURL.replace("{product_id}", productId),
				mergeDuplicateProductsRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getUpdatedEtilizeProduct = async (
	productId: string
): Promise<Product> => {
	try {
		const url =
			getUpdatedEtilizeProductURL.replace("{etilize_id}", productId) +
			`?product_source=etilize`;

		const response: AxiosResponse<Product> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
